import React, { useCallback, useEffect, useState } from "react";

import { CLUSTER_PALETTE } from "./portfolioClusterStyles";
import PortfolioMapGuideCard from "./PortfolioMapGuideCard";
import {
  guideColorWash,
  type GuideCell,
  isMatrixGuideLens,
} from "./portfolioMapGuides";
import { BH_CLASS_FILL, BH_CLASS_STROKE } from "./portfolioMapVisualHelpers";

interface Props {
  lensKey: string;
  cells: GuideCell[] | undefined;
  showGuide: boolean;
  onShowGuideChange: (open: boolean) => void;
  /** Show the Guide / Hide guide toggle button. Default true. */
  showToggle?: boolean;
  /** Show the colour/size legend row. Default true. */
  showLegend?: boolean;
  /** Hide the expandable card grid (overlays live on the chart). */
  hideCardGrid?: boolean;
  /** Align toggle to the end (top bar). */
  toggleAlign?: "start" | "end" | "between";
}

/**
 * Legend row + Guide toggle + optional guide-ref cards.
 * Scatter/matrix mount this twice: toggle at top, legend at bottom.
 * Guide content overlays the chart when open.
 */
const PortfolioMapGuidePanel: React.FC<Props> = ({
  lensKey,
  cells,
  showGuide,
  onShowGuideChange,
  showToggle = true,
  showLegend = true,
  hideCardGrid = false,
  toggleAlign = "between",
}) => {
  const [guideDetail, setGuideDetail] = useState<{
    cell: GuideCell;
    top: number;
    left: number;
  } | null>(null);

  const matrix = isMatrixGuideLens(lensKey);
  const cols = matrix ? 3 : 2;
  const showCards =
    showGuide && !hideCardGrid && Boolean(cells && cells.length > 0);

  useEffect(() => {
    setGuideDetail(null);
  }, [lensKey]);

  useEffect(() => {
    if (!showGuide) setGuideDetail(null);
  }, [showGuide]);

  const showGuideDetail = useCallback(
    (
      e: React.MouseEvent<HTMLElement> | React.FocusEvent<HTMLElement>,
      cell: GuideCell
    ) => {
      const box = e.currentTarget.getBoundingClientRect();
      const detailW = Math.min(310, window.innerWidth - 24);
      const detailH = 200;
      let left = box.right + 10;
      if (left + detailW > window.innerWidth - 10) {
        left = box.left - detailW - 10;
      }
      left = Math.max(10, left);
      let top = box.top;
      if (top + detailH > window.innerHeight - 10) {
        top = window.innerHeight - detailH - 10;
      }
      setGuideDetail({ cell, top: Math.max(10, top), left });
    },
    []
  );

  const guideToggle =
    showToggle && cells && cells.length > 0 ? (
      <button
        type="button"
        onClick={() => onShowGuideChange(!showGuide)}
        className={`shrink-0 rounded-md border px-2.5 py-1 text-[11.5px] font-semibold ${
          showGuide
            ? "border-brand bg-brand-faint text-brand"
            : "border-gray-200 bg-white text-gray-600 hover:border-purple-300 hover:text-purple-700"
        }`}
      >
        {showGuide ? "Hide guide ▵" : "View Guide ▿"}
      </button>
    ) : null;

  const legend = showLegend ? (
    <div className="flex flex-wrap items-center gap-3.5 text-[11px] text-gray-400">
      {matrix ? (
        <>
          <span className="mr-0.5 font-bold text-gray-400">
            Fill = exact AH cluster:
          </span>
          <span className="flex items-center gap-[5px]">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ background: CLUSTER_PALETTE.triage.color }}
            />
            Triage
          </span>
          <span className="flex items-center gap-[5px]">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ background: CLUSTER_PALETTE.defend.color }}
            />
            Defend
          </span>
          <span className="flex items-center gap-[5px]">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ background: CLUSTER_PALETTE.grow.color }}
            />
            Monitor
          </span>
          <span className="flex items-center gap-[5px]">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ background: CLUSTER_PALETTE.expand.color }}
            />
            Expand
          </span>
          <span className="ml-2 font-bold text-gray-400">
            Size = revenue · Halo = urgency
          </span>
          <span className="font-semibold text-brand">
            Click a cell to select accounts
          </span>
        </>
      ) : (
        <>
          <span className="mr-0.5 font-bold text-gray-400">
            Bubble colour = BH class:
          </span>
          <span className="flex items-center gap-[5px]">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full border-[1.5px]"
              style={{
                background: BH_CLASS_FILL.concern,
                borderColor: BH_CLASS_STROKE.concern,
              }}
            />
            Concern
          </span>
          <span className="flex items-center gap-[5px]">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full border-[1.5px]"
              style={{
                background: BH_CLASS_FILL.stable,
                borderColor: BH_CLASS_STROKE.stable,
              }}
            />
            Stable
          </span>
          <span className="flex items-center gap-[5px]">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full border-[1.5px]"
              style={{
                background: BH_CLASS_FILL.strong,
                borderColor: BH_CLASS_STROKE.strong,
              }}
            />
            Strong
          </span>
          <span className="ml-2 font-bold text-gray-400">
            Size + halo = urgency
          </span>
          <span className="font-semibold text-brand">
            Click a quadrant to select accounts
          </span>
        </>
      )}
    </div>
  ) : null;

  const cardGrid = showCards ? (
    <div
      className={`grid gap-1.5 rounded-lg border border-gray-200 bg-white p-2.5 ${
        cols === 3 ? "grid-cols-3" : "grid-cols-2"
      }`}
    >
      {cells!.map((cell, i) => (
        <div
          key={`${cell.name}-${i}`}
          tabIndex={0}
          role="button"
          className="cursor-help rounded-[5px] border-l-[3px] px-2 py-1.5 outline-none hover:shadow-[0_0_0_2px_rgba(118,74,188,0.18)] focus-visible:shadow-[0_0_0_2px_rgba(118,74,188,0.18)]"
          style={{
            background: guideColorWash(cell.color),
            borderLeftColor: cell.color,
          }}
          onMouseEnter={e => showGuideDetail(e, cell)}
          onMouseLeave={() => setGuideDetail(null)}
          onFocus={e => showGuideDetail(e, cell)}
          onBlur={() => setGuideDetail(null)}
        >
          <div
            className="mb-0.5 text-[10.5px] font-extrabold"
            style={{ color: cell.color }}
          >
            {cell.name}
          </div>
          <div className="text-[9.5px] leading-[1.35] text-gray-500">
            {cell.desc}
          </div>
          <div className="mt-[5px] text-[9px] font-bold text-gray-500">
            ⓘ Recommended direction
          </div>
        </div>
      ))}
    </div>
  ) : null;

  const alignClass =
    toggleAlign === "end"
      ? "justify-end"
      : toggleAlign === "start"
        ? "justify-start"
        : "justify-between";

  const hasBar = Boolean(legend || guideToggle);

  return (
    <>
      {hasBar && (
        <div className={`flex flex-wrap items-center gap-2 ${alignClass}`}>
          {legend}
          {guideToggle}
        </div>
      )}

      {cardGrid}

      {guideDetail && (
        <PortfolioMapGuideCard
          cell={guideDetail.cell}
          variant="card"
          className="pointer-events-none fixed z-[9998] w-[min(310px,calc(100vw-24px))]"
          style={{ top: guideDetail.top, left: guideDetail.left }}
        />
      )}
    </>
  );
};

export default PortfolioMapGuidePanel;
